import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Pagination from './Pagination';
import {
  MagnifyingGlassIcon,
  CubeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { format, parseISO } from 'date-fns';
import Badge from './ui/Badge';
import { appCategoryTone } from './ui/badgeTones';

function formatTimestamp(ts) {
  if (!ts) return '\u2014';
  try {
    return format(parseISO(ts), 'MMM d HH:mm');
  } catch {
    return ts;
  }
}

const ENTERPRISE_COLUMNS = [
  { id: '_expand',         label: '',           sortable: false, align: 'center' },
  { id: 'displayName',    label: 'App Name',   sortable: true,  align: 'left'   },
  { id: 'appId',          label: 'App ID',     sortable: true,  align: 'left'   },
  { id: 'signInEvents',   label: 'Sign-Ins',   sortable: true,  align: 'center' },
  { id: 'exchangeEvents', label: 'Exchange',   sortable: true,  align: 'center' },
  { id: 'totalEvents',    label: 'Total',      sortable: true,  align: 'center' },
  { id: 'ipsUsed',        label: 'IPs Used',   sortable: false, align: 'center' },
  { id: 'isKnown',        label: 'Known?',     sortable: true,  align: 'center' },
  { id: 'category',       label: 'Category',   sortable: true,  align: 'left'   },
  { id: 'operations',     label: 'Operations', sortable: false, align: 'left'   },
  { id: 'firstSeen',      label: 'First Seen', sortable: true,  align: 'left'   },
  { id: 'lastSeen',       label: 'Last Seen',  sortable: true,  align: 'left'   },
];

const ENTERPRISE_WIDTHS_INIT = [32, 300, 310, 80, 80, 80, 80, 80, 120, 320, 130, 130];

export default function EnterpriseAppsTab({
  apps = [],
  knownApps = [],
  tenantId,
  onToggleKnownApp,
  isSaving = false,
  isAdmin = false,
  hideKnownApps = true,
}) {
  const [filterQuery, setFilterQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('totalEvents');
  const [sortDirection, setSortDirection] = useState('desc');
  const [colWidths, setColWidths] = useState(ENTERPRISE_WIDTHS_INIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expandedApp, setExpandedApp] = useState(null);
  const [openMenuAppId, setOpenMenuAppId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  // Close dropdown on outside click or scroll
  useEffect(() => {
    if (!openMenuAppId) return;
    const close = () => setOpenMenuAppId(null);
    document.addEventListener('click', close);
    document.addEventListener('scroll', close, true);
    return () => { document.removeEventListener('click', close); document.removeEventListener('scroll', close, true); };
  }, [openMenuAppId]);

  // Build a set of known app IDs from live API data (overrides blob's isKnown)
  const knownAppMap = useMemo(() => {
    const map = new Map();
    knownApps.forEach((ka) => {
      map.set(ka.appId, ka);
    });
    return map;
  }, [knownApps]);

  // Merge live known app status into blob apps
  const effectiveApps = useMemo(() => {
    return apps.map((app) => {
      const liveKnown = knownAppMap.get(app.appId);
      if (liveKnown) {
        return {
          ...app,
          isKnown: true,
          category: liveKnown.category || app.category,
          _knownSource: liveKnown.tenantId === 'GLOBAL' ? 'Global' : 'Tenant',
        };
      }
      return { ...app, _knownSource: app.isKnown ? 'Runbook' : null };
    });
  }, [apps, knownAppMap]);

  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };

    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const processedApps = useMemo(() => {
    let result = [...effectiveApps];

    if (hideKnownApps) {
      result = result.filter((app) => !app.isKnown);
    }

    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      result = result.filter(
        (app) =>
          (app.displayName && app.displayName.toLowerCase().includes(q)) ||
          (app.appId && app.appId.toLowerCase().includes(q)) ||
          (app.category && app.category.toLowerCase().includes(q))
      );
    }

    result.sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      let comparison;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        comparison = (aVal ? 1 : 0) - (bVal ? 1 : 0);
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }
      return sortDirection === 'desc' ? -comparison : comparison;
    });

    return result;
  }, [effectiveApps, filterQuery, hideKnownApps, sortColumn, sortDirection]);

  const stats = useMemo(() => {
    const total = effectiveApps.length;
    const known = effectiveApps.filter((a) => a.isKnown).length;
    const unknown = total - known;
    return { total, known, unknown };
  }, [effectiveApps]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  useEffect(() => { setCurrentPage(1); setExpandedApp(null); }, [processedApps]);

  if (apps.length === 0) {
    return (
      <div className="card text-center py-16">
        <CubeIcon className="h-12 w-12 text-gray-700 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">
          No Enterprise App Data
        </h3>
        <p className="text-sm text-gray-600 max-w-md mx-auto">
          Enterprise application data will appear here after running an investigation.
        </p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(processedApps.length / pageSize));
  const pagedApps = processedApps.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const minTableWidth = colWidths.reduce((s, w) => s + w, 0);

  return (
    <div>
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border p-4 bg-blue-900/30 border-blue-700/30">
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
            Total Apps
          </span>
          <p className="text-2xl font-bold text-gray-100 mt-1">{stats.total}</p>
        </div>
        <div className="rounded-xl border p-4 bg-green-900/30 border-green-700/30">
          <div className="flex items-center gap-1.5">
            <CheckCircleIcon className="h-4 w-4 text-green-500" />
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
              Known
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-100 mt-1">{stats.known}</p>
        </div>
        <div className="rounded-xl border p-4 bg-amber-900/30 border-amber-700/30">
          <div className="flex items-center gap-1.5">
            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
              Unknown
            </span>
          </div>
          <p className="text-2xl font-bold text-gray-100 mt-1">{stats.unknown}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Filter by app name or ID..."
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            className="input-field pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {processedApps.length} app{processedApps.length !== 1 ? 's' : ''}
          </span>
          {isSaving && (
            <span className="text-xs text-blue-400">Saving...</span>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            ref={tableRef}
            className="text-sm"
            style={{ tableLayout: 'fixed', width: minTableWidth + 'px' }}
          >
            <thead>
              <tr className="border-b border-gray-800">
                {ENTERPRISE_COLUMNS.map((col, i) => (
                  <th
                    key={col.id}
                    ref={el => { thRefs.current[i] = el; }}
                    style={{ width: colWidths[i] + 'px' }}
                    onClick={col.sortable ? () => handleSort(col.id) : undefined}
                    className={`relative text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 overflow-hidden ${
                      col.align === 'center' ? 'text-center' : 'text-left'
                    }${col.sortable ? ' cursor-pointer hover:text-gray-300 select-none whitespace-nowrap' : ''}`}
                  >
                    <span className={`flex items-center${col.align === 'center' ? ' justify-center' : ''}`}>
                      {col.label}
                      {col.sortable && sortColumn === col.id && (
                        <span className="ml-1 text-blue-400">
                          {sortDirection === 'asc' ? '\u25B2' : '\u25BC'}
                        </span>
                      )}
                    </span>
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => startResize(e, i)}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {pagedApps.map((app, idx) => {
                const isExpanded = expandedApp === idx;
                return (
                  <React.Fragment key={app.appId}>
                    <tr
                      onClick={() => setExpandedApp(isExpanded ? null : idx)}
                      className={`hover:bg-gray-800/50 cursor-pointer transition-colors ${
                        app.isKnown
                          ? 'border-l-4 border-l-gray-600 opacity-60'
                          : 'border-l-4 border-l-amber-500'
                      }`}
                    >
                      <td className="px-1 py-3 text-center">
                        <ChevronRightIcon
                          className={`h-3.5 w-3.5 text-gray-600 transition-transform mx-auto ${isExpanded ? 'rotate-90' : ''}`}
                        />
                      </td>
                      <td className={`px-4 py-3 overflow-hidden truncate ${app.isKnown ? 'text-green-400' : 'text-gray-300'}`} title={app.displayName}>
                        {app.displayName || '\u2014'}
                      </td>
                      <td className={`px-4 py-3 font-mono text-xs overflow-hidden truncate ${app.isKnown ? 'text-green-400' : 'text-gray-400'}`} title={app.appId}>
                        {app.appId}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-400 font-mono overflow-hidden">
                        {app.signInEvents ?? 0}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-400 font-mono overflow-hidden">
                        {app.exchangeEvents ?? 0}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-200 font-mono font-semibold overflow-hidden">
                        {app.totalEvents ?? 0}
                      </td>
                      <td className="px-4 py-3 text-center overflow-hidden">
                        {app.ipsUsed && app.ipsUsed.length > 0 ? (
                          <span className="font-mono text-xs text-gray-400">
                            {app.ipsUsed.length}
                          </span>
                        ) : (
                          <span className="text-gray-600">{'\u2014'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center overflow-hidden">
                        {onToggleKnownApp ? (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (app.isKnown) {
                                  onToggleKnownApp(app.appId, app.displayName, false, 'tenant');
                                } else if (isAdmin) {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setMenuPos({ top: rect.bottom + 4, left: rect.right - 208 });
                                  setOpenMenuAppId(openMenuAppId === app.appId ? null : app.appId);
                                } else {
                                  onToggleKnownApp(app.appId, app.displayName, true, 'tenant');
                                }
                              }}
                              disabled={isSaving}
                              className={`p-0.5 rounded transition-colors ${
                                app.isKnown
                                  ? 'text-green-400 hover:text-red-400'
                                  : 'text-gray-600 hover:text-green-400'
                              }`}
                              title={app.isKnown ? `Remove Known flag (${app._knownSource || 'Known'})` : 'Flag as Known App'}
                            >
                              {app.isKnown ? (
                                <CheckCircleIcon className="h-5 w-5 mx-auto" />
                              ) : (
                                <PlusCircleIcon className="h-5 w-5 mx-auto" />
                              )}
                            </button>
                            {isAdmin && openMenuAppId === app.appId && !app.isKnown && createPortal(
                              <div
                                onClick={(e) => e.stopPropagation()}
                                style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
                                className="w-52 rounded-lg border border-gray-700 bg-gray-800 shadow-xl py-1"
                              >
                                <button
                                  onClick={() => { setOpenMenuAppId(null); onToggleKnownApp(app.appId, app.displayName, true, 'tenant'); }}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700/60"
                                >
                                  <BuildingOfficeIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  Flag for this Tenant
                                </button>
                                <button
                                  onClick={() => { setOpenMenuAppId(null); onToggleKnownApp(app.appId, app.displayName, true, 'global'); }}
                                  className="flex items-center gap-2 w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700/60"
                                >
                                  <GlobeAltIcon className="h-4 w-4 text-green-400 flex-shrink-0" />
                                  Flag Globally (all tenants)
                                </button>
                              </div>,
                              document.body
                            )}
                          </>
                        ) : (
                          app.isKnown ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-400 mx-auto" />
                          ) : (
                            <ExclamationTriangleIcon className="h-4 w-4 text-amber-400 mx-auto" />
                          )
                        )}
                      </td>
                      <td className="px-4 py-3 overflow-hidden">
                        {app.category ? (
                          <Badge tone={appCategoryTone(app.category)} dot>{app.category}</Badge>
                        ) : (
                          <span className="text-gray-600">{'\u2014'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs overflow-hidden truncate" title={Array.isArray(app.operations) ? app.operations.join(', ') : app.operations}>
                        {Array.isArray(app.operations) ? app.operations.join(', ') : (app.operations || '\u2014')}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs overflow-hidden whitespace-nowrap">
                        {formatTimestamp(app.firstSeen)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs overflow-hidden whitespace-nowrap">
                        {formatTimestamp(app.lastSeen)}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-800/30">
                        <td colSpan={ENTERPRISE_COLUMNS.length} className="px-6 py-4">
                          <div className="space-y-4">
                            {/* App Identity */}
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Application Details</h4>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <span className="text-xs text-gray-600 block">App Name</span>
                                  <span className={`text-sm break-all ${app.isKnown ? 'text-green-400' : 'text-gray-300'}`}>{app.displayName || '\u2014'}</span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-600 block">App ID</span>
                                  <span className={`text-sm font-mono break-all ${app.isKnown ? 'text-green-400' : 'text-gray-300'}`}>{app.appId}</span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-600 block">Category</span>
                                  <span className="text-sm text-gray-300">{app.category || 'Uncategorized'}</span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-600 block">Status</span>
                                  <span className="text-sm text-gray-300">
                                    {app.isKnown ? `Known (${app._knownSource || 'Runbook'})` : 'Unknown'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* IPs Used */}
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                IP Addresses ({app.ipsUsed?.length || 0})
                              </h4>
                              {app.ipsUsed && app.ipsUsed.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {app.ipsUsed.map((ip) => (
                                    <span
                                      key={ip}
                                      className="inline-flex items-center px-2.5 py-1 rounded bg-gray-700/60 border border-gray-600/50 font-mono text-xs text-gray-300"
                                    >
                                      {ip}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-600">No IP data available</span>
                              )}
                            </div>

                            {/* Operations */}
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                Operations ({Array.isArray(app.operations) ? app.operations.length : 0})
                              </h4>
                              {Array.isArray(app.operations) && app.operations.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {app.operations.map((op) => (
                                    <span
                                      key={op}
                                      className="inline-flex items-center px-2.5 py-1 rounded bg-blue-900/40 border border-blue-700/40 text-xs text-blue-300"
                                    >
                                      {op}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-600">No operation data available</span>
                              )}
                            </div>

                            {/* Activity Window */}
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Activity Window</h4>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <span className="text-xs text-gray-600 block">First Seen</span>
                                  <span className="text-sm text-gray-300">{formatTimestamp(app.firstSeen)}</span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-600 block">Last Seen</span>
                                  <span className="text-sm text-gray-300">{formatTimestamp(app.lastSeen)}</span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-600 block">Sign-In Events</span>
                                  <span className="text-sm text-gray-300 font-mono">{app.signInEvents ?? 0}</span>
                                </div>
                                <div>
                                  <span className="text-xs text-gray-600 block">Exchange Events</span>
                                  <span className="text-sm text-gray-300 font-mono">{app.exchangeEvents ?? 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {processedApps.length === 0 && (
                <tr>
                  <td colSpan={ENTERPRISE_COLUMNS.length} className="px-6 py-12 text-center">
                    <p className="text-gray-500">
                      {filterQuery || hideKnownApps
                        ? 'No apps match the current filters.'
                        : 'No enterprise app data found.'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={processedApps.length}
          onPageChange={(p) => { setCurrentPage(p); setExpandedApp(null); }}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); setExpandedApp(null); }}
        />
      </div>
    </div>
  );
}
